/**
 * Read axis-aligned editor bounds from a PageElement `base` object (wire / protobuf JSON).
 *
 * After PUT …/elements/rotate, some services persist the post-transform frame in
 * `visibleX` / `visibleY` / `visibleWidth` / `visibleHeight` while `x` / `y` may
 * remain in another frame. The editor and object panel use the visible rect; decode
 * paths must prefer it when present so reload matches the live canvas.
 *
 * NSD anchors every real element at its **center** (`base.anchorRef` / XML `AR` attribute —
 * every element observed in practice is `AR="CTR"`; `geometricX/Y` and `visibleX/Y` are
 * documented as "center X/Y" too, per `larry-common-library/src/protobuf/schema.proto`). `ShapeData` /
 * `TextBoxData` store the top-left corner, so every rect read here shifts by half the
 * element's own size after projecting — see `centerRectToTopLeftDocPx`.
 */

import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION, centeredRectToDocPx } from '@/lib/canvas/document-projection'

/** NSD center-anchored rect → doc-px top-left rect. Width/height are frame- and anchor-invariant. */
function centerRectToTopLeftDocPx(
  r: { x: number; y: number; width: number; height: number },
  proj: DocumentProjection
): { x: number; y: number; width: number; height: number } {
  const docPx = centeredRectToDocPx(r, proj)

  return {
    x: docPx.x - docPx.width / 2,
    y: docPx.y - docPx.height / 2,
    width: docPx.width,
    height: docPx.height,
  }
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value !== '') {
    const n = Number(value)

    return Number.isFinite(n) ? n : null
  }

  return null
}

/** Degrees in [0, 360). */
function canonicalRotationDegrees(rotation: number): number {
  let r = rotation % 360

  if (r < 0) r += 360
  if (r >= 360 - 1e-9) r = 0

  return r
}

/** True when editor/checkpoint hydration should use `base.visible*` instead of `base.x`/`base.y`. */
export function elementUsesVisibleEditorRect(rotationDeg: number): boolean {
  const c = canonicalRotationDegrees(rotationDeg)

  return c > 1e-6 && c < 360 - 1e-6
}

export function readPageElementEditorRectFromBase(
  base: Record<string, unknown>,
  projection: DocumentProjection = IDENTITY_PROJECTION
): { x: number; y: number; width: number; height: number } | null {
  const rotation = finiteNumber(base.rotation ?? base.Rotation) ?? 0

  const vx = finiteNumber(base.visibleX ?? base.VisibleX)
  const vy = finiteNumber(base.visibleY ?? base.VisibleY)
  const vw = finiteNumber(base.visibleWidth ?? base.VisibleWidth)
  const vh = finiteNumber(base.visibleHeight ?? base.VisibleHeight)

  // Unrotated elements often carry `visible*` as protobuf defaults (0,0,0,0). Preferring
  // them would wipe real `x`/`y`/`width`/`height` and hide shapes after reload.
  const visibleLooksUsable =
    vx != null &&
    vy != null &&
    vw != null &&
    vh != null &&
    vw > 0 &&
    vh > 0 &&
    elementUsesVisibleEditorRect(rotation)

  if (visibleLooksUsable) {
    return centerRectToTopLeftDocPx({ x: vx, y: vy, width: vw, height: vh }, projection)
  }

  const x = base.x == null ? 0 : finiteNumber(base.x)
  const y = base.y == null ? 0 : finiteNumber(base.y)
  const width = finiteNumber(base.width ?? base.Width)
  const height = finiteNumber(base.height ?? base.Height)

  if (x == null || y == null || width == null || height == null) return null

  return centerRectToTopLeftDocPx({ x, y, width, height }, projection)
}
